import { getClientAccountPlanStats } from '@/api/clientAccounts';
import { getPaymentPlans } from '@/api/paymentPlans';
import { IClientAccountStatsByType } from '@/models/clientAccount';
import { IPaymentPlan } from '@/models/paymentPlan';
import { ActionContext } from 'vuex';

import { IState } from '..';
import { IObjectState, IStateObject } from '../';

export interface IStatePaymentPlan extends IObjectState, IPaymentPlan {
  type: keyof IClientAccountStatsByType;
  account_count: number;
  active_account_count: number;
  monthly_price_eur_display: string;
  annual_price_eur_display: string;
  one_time_price_eur_display: string;
  monthly_price_usd_display: string;
  annual_price_usd_display: string;
  one_time_price_usd_display: string;
}

export interface IPaymentPlansState extends IStateObject<IStatePaymentPlan> {
  statsById: Record<number, { account_count: number, active_account_count: number }>;
}

const state: IPaymentPlansState = {
  list: [],
  selected: [],
  loading: false,
  message: '',
  error: false,
  statsById: {}
};

const deriveType = (plan: IPaymentPlan): keyof IClientAccountStatsByType => {
  if (plan.head_counter_service) return 'headcounter';
  if (plan.check_in_app_service) return 'check_in';
  if (plan.beamian_accounts_service) return 'beamian';
  return 'other';
};

const formatPrice = (cents: number, currency: string): string =>
  `${(cents / 100).toFixed(2)} ${currency}`;

const addStateProps = (
  plans: IPaymentPlan[],
  statsById: Record<number, { account_count: number, active_account_count: number }>
): IStatePaymentPlan[] =>
  plans.map(plan => ({
    ...plan,
    type: deriveType(plan),
    account_count: statsById[plan.id] ? statsById[plan.id].account_count : 0,
    active_account_count: statsById[plan.id] ? statsById[plan.id].active_account_count : 0,
    monthly_price_eur_display: formatPrice(plan.monthly_price_eur, 'EUR'),
    annual_price_eur_display: formatPrice(plan.annual_price_eur, 'EUR'),
    one_time_price_eur_display: formatPrice(plan.one_time_price_eur, 'EUR'),
    monthly_price_usd_display: formatPrice(plan.monthly_price_usd, 'USD'),
    annual_price_usd_display: formatPrice(plan.annual_price_usd, 'USD'),
    one_time_price_usd_display: formatPrice(plan.one_time_price_usd, 'USD'),
    loading: false,
    error: false,
    message: ''
  }));

const getters = {
  getPaymentPlans: (state: IPaymentPlansState) => state.list,
  getSelectedPaymentPlans: (state: IPaymentPlansState) => state.selected,
  getPaymentPlansState: (state: IPaymentPlansState) => state,
};

const fetchAllPages = (
  page: number,
  pageSize: number,
  includeInactive: boolean,
  accumulated: IPaymentPlan[]
): Promise<IPaymentPlan[]> =>
  getPaymentPlans(page, pageSize, includeInactive).then((response: any) => {
    const results = accumulated.concat(response.data.results);
    if (page < response.data.page_count) {
      return fetchAllPages(page + 1, pageSize, includeInactive, results);
    }
    return results;
  });

const actions = {

  // Plan counts are small (a handful of plans), so it's fine to fetch the
  // full list at once rather than paginate on screen.
  fetchPaymentPlans(
    context: ActionContext<IPaymentPlansState, IState>,
    params?: { includeInactive?: boolean }
  ) {

    context.commit('setPaymentPlansLoading');

    fetchAllPages(1, 100, !!params?.includeInactive, [])
      .then(plans => {
        context.commit('setPaymentPlans', addStateProps(plans, context.state.statsById));
      })
      .catch(err => {
        context.commit('setPaymentPlansError', err.message);
      });

  },

  // Best-effort fetch used only to enrich plans with account counts. Runs
  // independently so a failure (e.g. non-admin user, since this endpoint is
  // IsAdminUser only) never blocks the plans list from rendering.
  fetchPaymentPlanStats(context: ActionContext<IPaymentPlansState, IState>) {

    getClientAccountPlanStats()
      .then(response => {
        const statsById: Record<number, { account_count: number, active_account_count: number }> = {};
        (response.data.plans || []).forEach(plan => {
          statsById[plan.id] = {
            account_count: plan.account_count,
            active_account_count: plan.active_account_count
          };
        });
        context.commit('setStatsById', statsById);
      })
      .catch(() => {
        // Silently ignore: account counts will fall back to 0.
      });

  },

};

const mutations = {

  setPaymentPlansError: (state: IPaymentPlansState, message: string) => (
    (state.loading = false),
    (state.error = true),
    (state.message = message)
  ),

  setPaymentPlansLoading: (state: IPaymentPlansState) => (
    (state.loading = true),
    (state.error = false)
  ),

  setPaymentPlans: (state: IPaymentPlansState, plans: IStatePaymentPlan[]) => (
    (state.error = false),
    (state.loading = false),
    (state.list = plans)
  ),

  setStatsById: (
    state: IPaymentPlansState,
    statsById: Record<number, { account_count: number, active_account_count: number }>
  ) => {
    state.statsById = statsById;
    if (state.list.length) {
      state.list = state.list.map(plan => ({
        ...plan,
        account_count: statsById[plan.id] ? statsById[plan.id].account_count : 0,
        active_account_count: statsById[plan.id] ? statsById[plan.id].active_account_count : 0,
      }));
    }
  },

  selectPaymentPlan: (state: IPaymentPlansState, plan: IStatePaymentPlan) => {
    if (plan.id) {
      const index = state.selected.indexOf(plan.id);
      if (index > -1) {
        state.selected.splice(index, 1);
      } else {
        state.selected.push(plan.id);
      }
    }
  },

};

export default {
  state,
  getters,
  actions,
  mutations
};
